import './Main.css';
import '../../styles/nav-icon.css';

import {useState} from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {useAuth} from '../../contexts/AuthContext';
import {useUserInfo} from '../../hooks/useUserInfo';
import {DashboardHeader} from '../common';
import Baseline from './Baseline/Baseline';
import Dashboard from './Dashboard/Dashboard';
import HomePage from './HomePage/HomePage';
import {Impairment} from './Impairment';
import Settings from './Settings';
import Team from './Team/Team';

const computeHeaderLabel = (data) => {
  if (!data) return '';
  if (data.role === 'admin') return 'Admin Dashboard';
  if ((data.orgCount ?? 0) > 1) return 'Manager Dashboard';
  return data.companyName
    ? `${data.companyName} Manager Dashboard`
    : 'Manager Dashboard';
};

const Main = () => {
  const {currentUser, logout} = useAuth();
  const {data: userInfo} = useUserInfo();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const headerLabel = computeHeaderLabel(userInfo);

  const handleLogout = async () => {
    setError('');
    setLoading(true);
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      setError('Failed to log out');
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
    }
  };

  const isActive = (path) =>
    location.pathname === path ||
    (path !== '/dashboard' && location.pathname.startsWith(path));

  return (
    <div className='dashboard-container'>
      <div className='burger-button'>
        <button className='nav-icon-btn' onClick={() => setMenuOpen(!menuOpen)}>
          <div
            className={`nav-icon ${menuOpen ? 'nav-icon--active' : ''}`}
          ></div>
        </button>
      </div>

      {menuOpen && (
        <div className='overlay' onClick={() => setMenuOpen(false)} />
      )}

      <div className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <div className='logo'>Tenvos AI</div>
        <nav className='nav-menu'>
          <Link
            to='/dashboard'
            className={`nav-item ${
              isActive('/dashboard') && location.pathname === '/dashboard'
                ? 'active'
                : ''
            }`}
            onClick={() => setMenuOpen(false)}
          >
            Home
          </Link>
          <Link
            to='/dashboard/dashboard'
            className={`nav-item ${isActive('/dashboard/dashboard') ? 'active' : ''}`}
            onClick={() => setMenuOpen(false)}
          >
            Dashboard
          </Link>
          <Link
            to='/dashboard/impairment'
            className={`nav-item ${isActive('/dashboard/impairment') ? 'active' : ''}`}
            onClick={() => setMenuOpen(false)}
          >
            Impairment
          </Link>
          <Link
            to='/dashboard/baseline'
            className={`nav-item ${isActive('/dashboard/baseline') ? 'active' : ''}`}
            onClick={() => setMenuOpen(false)}
          >
            Baseline
          </Link>
          <Link
            to='/dashboard/team'
            className={`nav-item ${isActive('/dashboard/team') ? 'active' : ''}`}
            onClick={() => setMenuOpen(false)}
          >
            Team
          </Link>
          <Link
            to='/dashboard/settings'
            className={`nav-item ${isActive('/dashboard/settings') ? 'active' : ''}`}
            onClick={() => setMenuOpen(false)}
          >
            Settings
          </Link>
        </nav>
      </div>

      <div className='main-area'>
        <header className='dashboard-header'>
          <DashboardHeader label={headerLabel} />
          <div className='user-info'>
            <span>{currentUser?.email}</span>
            <button
              onClick={handleLogout}
              className='logout-button'
              disabled={loading}
            >
              {loading ? 'Logging out...' : 'Logout'}
            </button>
          </div>
        </header>

        {error && <div className='dashboard-error'>{error}</div>}

        <main>
          <Routes>
            <Route path='/' element={<HomePage />} />
            <Route path='/settings' element={<Settings />} />
            <Route path='/dashboard' element={<Dashboard />} />
            <Route path='/impairment' element={<Impairment />} />
            <Route path='/baseline' element={<Baseline />} />
            <Route path='/team' element={<Team />} />
            <Route path='*' element={<Navigate to='/dashboard' replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default Main;
